import { StatusTag } from 'components/core/Chips/StatusTag/StatusTag';
import { MdReplay, MdCancel } from 'react-icons/md';
import type { ComponentType } from 'react';
import { useCurrencyFormat } from 'hooks/useCurrencyFormat';
import { useTitleCase } from 'hooks/useTitleCase';
import moment from 'moment-timezone';
import { DataListListProps } from 'shared/models/components/base.model';
import {
  getApprovalsStatusSorting,
  getNameFromAccount,
  getVendorNameSorting,
} from 'shared/utilities/utils';
import { CellContent } from '../CellElements/CellContent/CellContent';
import { DateRangeColumnFilter, MultiCheckBoxColumnFilter } from '../CellElements/Filters';
import { Transactions } from './ColumnWidths';
import { handleCheckStatus } from 'helpers/modules/helperInvoices';
import { CheckImageUrlModal } from 'components/core/Modals/CheckImageUrlModal/CheckImageUrlModal';
import { isPermitted } from 'services/userApi';
import { achReceiptExportUrl, achReceiptHref } from 'shared/utilities/achReceipt';
import type {
  MarkPbtStatusAction,
  MarkPbtStatusTransfer,
} from 'components/core/Modals/MarkPbtStatusModal/MarkPbtStatusModal';

const RefundIcon = MdReplay as unknown as ComponentType<{ size?: number }>;
const CancelIcon = MdCancel as unknown as ComponentType<{ size?: number }>;

export const isPbtEligibleForMarkStatus = (row: any): boolean => {
  if (!row) return false;
  const status = String(row.status ?? '').toLowerCase();
  const payMode = String(row.pay_mode ?? '').toLowerCase();
  if (status === 'refunded' || status === 'canceled' || status === 'cancelled') {
    return false;
  }
  if (payMode === 'ach') {
    return status !== 'paid';
  }
  if (payMode === 'check' || payMode === 'digital_check') {
    return !row.check_paid_on;
  }
  return false;
};

interface TransactionColumnProps extends DataListListProps {
  onMarkPbtStatusOpen?: (transfer: MarkPbtStatusTransfer, action: MarkPbtStatusAction) => void;
}

export const handleTransactionTableColumn = ({
  subTabType,
  user,
  onMarkPbtStatusOpen,
}: TransactionColumnProps) => {
  const transactionTableColumn: any[] = [
    {
      Header: 'ACCOUNT HOLDER',
      wrapperClasses: Transactions.ColomnWidth.vendor,
      headerClasses: Transactions.HeaderCellWidth.vendor,
      accessor: (row: any) => getVendorNameSorting(row.account),
      filter: 'matches',
      sortType: 'string',
      isReverseSortingIcon: true,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention,react/destructuring-assignment
        const { account } = row.row.original;

        return (
          <CellContent
            isLink={true}
            linkTarget="_self"
            linkHref={`/vendors/account/${account.id}`}
            isLinkWrapped
            wrapperClasses={Transactions.ColomnWidth.vendor}
            additionalClasses="capitalize"
          >
            {useTitleCase(getNameFromAccount(account))}
          </CellContent>
        );
      },
    },
    {
      Header: 'PROPERTY',
      accessor: 'property.name',
      wrapperClasses: Transactions.ColomnWidth.property_name,
      headerClasses: Transactions.HeaderCellWidth.property_name,
      filter: 'fuzzy',
      sortType: 'string',
      isReverseSortingIcon: true,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        const { property } = row.row.original;

        return (
          <CellContent
            isLink
            linkTarget="_self"
            linkHref={`/properties/profile/${property && property.id}`}
            wrapperClasses={Transactions.ColomnWidth.property_name}
            isTooltipEnabled={property && property.is_delinquent}
            tooltipIconClasses="text-red-500"
            tooltipIcon="DelinquentIcon"
            tooltipContent={`Property is delinquent`}
          >
            {property?.name ? useTitleCase(property?.name) : null}
          </CellContent>
        );
      },
    },
    {
      Header: 'STATUS',
      accessor: (row: any) =>
        getApprovalsStatusSorting(
          row.status,
          row.payout_scheduled_at,
          row.pay_mode,
          row.check_status
        ),
      filter: 'multiSelect',
      Filter: MultiCheckBoxColumnFilter,
      wrapperClasses: Transactions.ColomnWidth.status,
      headerClasses: Transactions.HeaderCellWidth.status,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        const {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          pay_mode,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          check_status,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          check_number,
          status: rawStatus,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          refunded_at,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          refund_notes,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          canceled_at,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          cancellation_notes,
        } = row.row.original;

        const normalizedStatus = String(rawStatus ?? '').toLowerCase();
        const isRefunded = normalizedStatus === 'refunded';
        const isCanceled = normalizedStatus === 'canceled' || normalizedStatus === 'cancelled';
        const isMarkedRefundedOrCanceled = isRefunded || isCanceled;

        let status: any = rawStatus;
        if (isMarkedRefundedOrCanceled) {
          status = isRefunded ? 'refunded' : 'canceled';
        } else if (
          (pay_mode === 'CHECK' || pay_mode === 'DIGITAL_CHECK') &&
          check_status !== null
        ) {
          status = handleCheckStatus(check_status);
        }

        const markedAt = isRefunded ? refunded_at : canceled_at;
        const markedNotes = isRefunded ? refund_notes : cancellation_notes;
        const markedAtText =
          markedAt && moment(markedAt).isValid()
            ? moment(markedAt).format('MM/DD/YYYY h:mm A')
            : null;
        const verb = isRefunded ? 'Refunded' : 'Canceled';
        const markedTooltip = isMarkedRefundedOrCanceled
          ? [
              check_status ? `Original check status: ${check_status}.` : null,
              markedAtText ? `${verb} on ${markedAtText}.` : null,
              markedNotes ? `Notes: ${markedNotes}` : null,
            ]
              .filter(Boolean)
              .join(' ')
          : null;

        const showCheckNumberTooltip =
          !isMarkedRefundedOrCanceled && pay_mode === 'CHECK' && check_number;

        return (
          <CellContent
            wrapperClasses={`flex flex-row w-full !justify-start justify-items-start !items-start ${Transactions.ColomnWidth.status}`}
            isWordWrapped
            isTooltipEnabled={!!markedTooltip || !!showCheckNumberTooltip}
            tooltipIconClasses="text-grey-600 relative -left-[5px] top-[5px] cursor-pointer"
            tooltipContainerClass={
              markedTooltip
                ? 'left-[8px] -top-[2px] rounded-tl-lg !min-w-[220px] !w-[260px]'
                : 'left-[8px] -top-[2px] rounded-tl-lg !min-w-[0px] !w-[150px]'
            }
            tooltipContent={markedTooltip ?? 'Check number : ' + check_number}
          >
            {status ? <StatusTag status={status} /> : null}
          </CellContent>
        );
      },
    },
    {
      Header: 'CREATED ON',
      accessor: 'created_at',
      Filter: DateRangeColumnFilter,
      filter: 'dateBetween',
      wrapperClasses: Transactions.ColomnWidth.created_at,
      headerClasses: Transactions.HeaderCellWidth.created_at,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const createdAt =
          row.row.values.created_at && moment(row.row.values.created_at).format('MM/DD/YYYY');

        return (
          <CellContent wrapperClasses={Transactions.ColomnWidth.created_at} isWordWrapped>
            {createdAt}
          </CellContent>
        );
      },
    },
    {
      Header: 'PAID ON',
      accessor: 'paid_at',
      Filter: DateRangeColumnFilter,
      filter: 'dateBetween',
      wrapperClasses: Transactions.ColomnWidth.paid_at,
      headerClasses: Transactions.HeaderCellWidth.paid_at,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const paidAt = row.row.values.paid_at
          ? moment(row.row.values.paid_at).format('MM/DD/YYYY')
          : null;

        return (
          <CellContent wrapperClasses={Transactions.ColomnWidth.paid_at} isWordWrapped>
            {paidAt}
          </CellContent>
        );
      },
    },
    {
      Header: 'SETTLED ON',
      accessor: 'expected_settlement_date',
      Filter: DateRangeColumnFilter,
      filter: 'dateBetween',
      wrapperClasses: Transactions.ColomnWidth.expected_settlement_date,
      headerClasses: Transactions.HeaderCellWidth.expected_settlement_date,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        let settlementVal = row.row.values.expected_settlement_date;
        // const date = new Date(row.row.values.expected_settlement_date);
        let TimeHours = moment(row.row.values.expected_settlement_date).utc().format('HH');

        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { pay_mode, check_paid_on } = row.row.original;
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const check_paid_at = check_paid_on ? check_paid_on : null;

        if (pay_mode === 'CHECK') {
          if (check_paid_at) {
            settlementVal = check_paid_at;
            TimeHours = moment(check_paid_at).utc().format('HH');
          }
        }

        const handleDate = () => {
          let settlementDate;

          if (settlementVal) {
            settlementDate = moment(settlementVal).format('MM/DD/YYYY');

            if (TimeHours >= '18') {
              // Check if it's after or equal 6:00pm
              settlementDate = moment(settlementVal)
                .add(1, 'day')
                .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
                .format('MM/DD/YYYY');
            }
          }

          return settlementDate;
        };

        return (
          <CellContent
            wrapperClasses={Transactions.ColomnWidth.expected_settlement_date}
            isWordWrapped
          >
            {handleDate()}
          </CellContent>
        );
      },
    },
    {
      Header: 'BATCH NUMBER',
      accessor: 'batch_number',
      filter: 'multiSelect',
      wrapperClasses: Transactions.ColomnWidth.batch_number,
      headerClasses: Transactions.HeaderCellWidth.batch_number,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const handleBatchNumber = () => {
          let batchNumber;

          if (row.row.values.batch_number === 0) {
            batchNumber = '0';
          } else if (row.row.values.batch_number !== 0 && row.row.values.batch_number) {
            batchNumber = row.row.values.batch_number;
          }

          return batchNumber;
        };

        return (
          <CellContent wrapperClasses={Transactions.ColomnWidth.batch_number} isWordWrapped>
            {handleBatchNumber()}
          </CellContent>
        );
      },
    },
    {
      Header: 'PAY MODE',
      accessor: 'pay_mode',
      wrapperClasses: Transactions.ColomnWidth.pay_mode,
      headerClasses: Transactions.HeaderCellWidth.pay_mode,
      Filter: MultiCheckBoxColumnFilter,
      filter: 'multiSelect',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention,react/destructuring-assignment
        const { pay_mode } = row.row.original;

        return <CellContent wrapperClasses={'w-100px'}>{pay_mode}</CellContent>;
      },
    },
    {
      Header: 'CHECK NUMBER',
      wrapperClasses: Transactions.ColomnWidth.check_image_url,
      headerClasses: Transactions.HeaderCellWidth.check_image_url,
      accessor: 'check_number',
      filter: 'fuzzy',
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention,react/destructuring-assignment
        const { check_image_url, check_number } = row.row.original;

        return (
          <CheckImageUrlModal imageURL={check_image_url} checkNumer={check_number} user={user} />
        );
      },
    },
    {
      Header: 'ACH RECEIPT',
      id: 'ach_receipt_url',
      // The CSV export writes this value; the cell below renders the link.
      accessor: (row: any) => achReceiptExportUrl(row),
      wrapperClasses: Transactions.ColomnWidth.ach_receipt_url,
      headerClasses: Transactions.HeaderCellWidth.ach_receipt_url,
      canFilter: false,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        // eslint-disable-next-line @typescript-eslint/naming-convention,react/destructuring-assignment
        const receiptHref = achReceiptHref(row.row.original);

        return (
          <CellContent
            wrapperClasses={Transactions.ColomnWidth.ach_receipt_url}
            isLink={!!receiptHref}
            linkHref={receiptHref ?? undefined}
          >
            {receiptHref ? 'View Receipt' : null}
          </CellContent>
        );
      },
    },
    {
      Header: 'ESTIMATED PROCESSING FEE',
      accessor: 'estimated_processing_fee_amount',
      headerClasses: Transactions.HeaderCellWidth.estimated_processing_fee_amount,
      headerInnerClasses: Transactions.HeaderInnerDivWidth.estimated_processing_fee_amount,
      canFilter: false,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        // eslint-disable-next-line react/destructuring-assignment,@typescript-eslint/naming-convention
        const { estimated_processing_fee_amount } = row.row.original;
        // eslint-disable-next-line react/destructuring-assignment
        const amountTerm = estimated_processing_fee_amount
          ? useCurrencyFormat(parseFloat(estimated_processing_fee_amount))
          : null;
        // eslint-disable-next-line react/destructuring-assignment
        return (
          <CellContent wrapperClasses={Transactions.ColomnWidth.estimated_processing_fee_amount}>
            {amountTerm}
          </CellContent>
        );
      },
    },
    {
      Header: 'INVOICE AMOUNT',
      accessor: 'amount',
      headerClasses: Transactions.HeaderCellWidth.amount,
      headerInnerClasses: Transactions.HeaderInnerDivWidth.amount,
      canFilter: false,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        // eslint-disable-next-line react/destructuring-assignment,@typescript-eslint/naming-convention
        const { amount } = row.row.original;
        // eslint-disable-next-line react/destructuring-assignment
        const amountMain = amount ? useCurrencyFormat(parseFloat(amount)) : null;
        // eslint-disable-next-line react/destructuring-assignment
        return (
          <CellContent wrapperClasses={Transactions.ColomnWidth.amount}>{amountMain}</CellContent>
        );
      },
    },
    {
      Header: 'PAID AMOUNT',
      accessor: 'paid_amount',
      headerClasses: Transactions.HeaderCellWidth.paid_amount,
      headerInnerClasses: Transactions.HeaderInnerDivWidth.paid_amount,
      canFilter: false,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        // eslint-disable-next-line react/destructuring-assignment,@typescript-eslint/naming-convention
        const { paid_amount } = row.row.original;
        // eslint-disable-next-line react/destructuring-assignment
        const amountPayout = paid_amount ? useCurrencyFormat(parseFloat(paid_amount)) : null;
        // eslint-disable-next-line react/destructuring-assignment
        return (
          <CellContent wrapperClasses={Transactions.ColomnWidth.paid_amount}>
            {amountPayout}
          </CellContent>
        );
      },
    },
    {
      Header: 'PROCESSING FEE',
      accessor: 'processing_fee_amount',
      headerClasses: Transactions.HeaderCellWidth.processing_fee_amount,
      headerInnerClasses: Transactions.HeaderInnerDivWidth.processing_fee_amount,
      canFilter: false,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        // eslint-disable-next-line react/destructuring-assignment,@typescript-eslint/naming-convention
        const { processing_fee_amount } = row.row.original;
        // eslint-disable-next-line react/destructuring-assignment
        const amountTerm = processing_fee_amount
          ? useCurrencyFormat(parseFloat(processing_fee_amount))
          : null;
        // eslint-disable-next-line react/destructuring-assignment
        return (
          <CellContent wrapperClasses={Transactions.ColomnWidth.processing_fee_amount}>
            {amountTerm}
          </CellContent>
        );
      },
    },
  ];

  if (onMarkPbtStatusOpen && isPermitted(user, 'pbt-mark-status')) {
    transactionTableColumn.push({
      Header: 'ACTIONS',
      id: 'mark_status_actions',
      accessor: 'id',
      canFilter: false,
      disableSortBy: true,
      wrapperClasses: Transactions.ColomnWidth.actions,
      headerClasses: Transactions.HeaderCellWidth.actions,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      Cell: (row) => {
        const original = row.row.original;
        if (!isPbtEligibleForMarkStatus(original)) {
          return (
            <div
              className={`${Transactions.ColomnWidth.actions} inline-flex items-center justify-center`}
            >
              <span className="text-12s text-gray-400">—</span>
            </div>
          );
        }
        return (
          <div
            className={`${Transactions.ColomnWidth.actions} inline-flex flex-row items-center gap-2`}
          >
            <button
              type="button"
              title="Mark refunded"
              aria-label="Mark refunded"
              onClick={() => onMarkPbtStatusOpen(original, 'refund')}
              className="group relative inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-black-900"
            >
              <RefundIcon size={18} />
              <span className="pointer-events-none absolute -top-7 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded bg-black-900 px-2 py-0.5 text-10s font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                Refund
              </span>
            </button>
            <button
              type="button"
              title="Mark canceled"
              aria-label="Mark canceled"
              onClick={() => onMarkPbtStatusOpen(original, 'cancel')}
              className="group relative inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-black-900"
            >
              <CancelIcon size={18} />
              <span className="pointer-events-none absolute -top-7 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded bg-black-900 px-2 py-0.5 text-10s font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                Cancel
              </span>
            </button>
          </div>
        );
      },
    });
  }

  return transactionTableColumn;
};
